/**
 * `onboarding-mark` (crossmarc/steward-app#1766 part 2, docs/35-advisor-client-onboarding.md R4
 * "Mark as onboarded") — the firm's own record that a prospect has been marked onboarded: who
 * did it and when. One row per `advisor-client` record ({@link ADVISOR_CLIENT_TYPE_CODE},
 * `advisorClient.ts`), in the firm's own workspace.
 *
 * ## Placement and shape, mirroring `client-about` EXACTLY (coordinator decision D1, 2026-09-25)
 *
 * Same pack (`STEWARD_ADVISOR_FIRM_TYPE_DEFS`), same write shape: the façade creates it under
 * the firm caller's own bearer token (`services/steward-app-api/src/handlers/onboardingMark.ts`),
 * never a background role, and `accessControl.writeCapability` is `STEWARD_WRITE_CAPABILITY` —
 * the same union `client-about`/`advisor-client`/`onboarding-invitation` document: no role in
 * THIS pack carries `steward:write`, but a firm workspace mounts `steward-app` too, and
 * `steward-admin`/`steward-member` there do (`packRoles.ts`). Per the coordinator: no new
 * capability, no role change, no appcore change, no actor rule.
 *
 * ## One row per client record, found by attribute — see `clientAbout.ts`'s own header
 *
 * Same constraint, same fix: appcore mints its own instanceId, so the façade lists the firm's
 * `onboarding-mark` rows and matches by the `clientId` attribute (the handler's `findMarkRow`).
 * Two concurrent FIRST marks for the same client can each see "no row" and each create one; that
 * race is not closed here, matching `client-about`'s own accepted, narrow cost (see that file's
 * header) — a `GET` reading the firm-wide list back returns whichever row `listAllEntities`
 * happens to return first, which is still one real mark by one real person, not a corrupted read.
 *
 * ## Immutable once created: marking twice is a no-op, not a re-stamp
 *
 * `markedByUserId`/`markedByName`/`markedAt` are the record of the FIRST mark. A second `POST`
 * for an already-marked client returns the EXISTING row unchanged — it does not move
 * `markedAt` forward or re-attribute the marker to whoever clicked twice. This is the opposite of `client-about`'s
 * merge-and-restamp behaviour, and deliberately so: an About field is edited repeatedly over a
 * relationship's life, while "who first marked this family onboarded" is a fact about one
 * moment, not a value to overwrite. There is no `expectedVersion` update path on this type at
 * all — the handler never calls `updateEntity` on it, only `createEntity` once and `getEntity`-
 * shaped reads thereafter.
 *
 * ## Not authority
 *
 * Nothing reads this row as authority over anything the family answered. It drives two things
 * only: the Prospects/Clients stage (R4 — a mark moves the record to `'client'`, the LAST rung
 * on `prospectStages.ts`'s ladder, so it outranks every other stage including `archived`,
 * matching R4's "allowed at any stage") and the who/when line on the client profile header (the
 * mock's "marked onboarded by Dana Lee"). Marking is a firm judgment call about a family being
 * ready, not a re-derivation of the onboarding form's own completeness — that stays
 * `onboardingFormStatus.ts`'s job, read live at mark time for the confirm dialog's warning about
 * what is still empty.
 *
 * ## Who may write it — stricter than `client-about`, by explicit decision
 *
 * `client-about`'s write needs no gate beyond ordinary firm membership (that file's own header,
 * "Authorization: no extra gate"). This type's write is gated MORE tightly, at the façade, not
 * on the type: `requireTeamAccess` (the caller must be this client's own assigned advisor or a
 * firm admin) AND `mayProvisionAtFacade` (the same role that gates an onboarding invitation
 * send/resend). Both are façade-only checks; `accessControl.writeCapability` here is exactly
 * `client-about`'s. The READ route is deliberately NOT team-gated, to match the firm-wide stage
 * join on `GET /advisor/client-records` (`clientRecords.ts`'s `firmOnboardingMarks`), which
 * already shows every firm member which prospects have been marked onboarded — team-gating the
 * single-client read more tightly than the list already is would be a narrower door beside a
 * wider window onto the same fact.
 *
 * ## What this type is NOT
 *
 *   - **Not copied into a Steward workspace.** Same reasoning as `client-about`'s own "What
 *     this type is NOT": setting up Steward copies the SSN and prefill data, never this row.
 *   - **Not readable by the family.** Same isolation as `client-about`: a client's own
 *     workspace membership carries no membership in the firm's workspace.
 *   - **Not a second onboarding-completeness record.** See "Not authority" above.
 *
 * ## Residual gap, matching `client-about`'s own note — NOT a live bypass today (dated with
 * that review, 2026-09-25, not re-run for this type specifically)
 *
 * Same shape, same mitigation: a `steward:write` holder could, if this type were reachable
 * directly, write a row naming any marker they like. It is not reachable directly on any
 * deployed instance — every committed instance profile declares `"memberPlane": "relay-only"`,
 * which routes appcore's generic entity CRUD behind the IAM (SigV4) authorizer, not the Cognito
 * JWT one a browser satisfies. See `clientAbout.ts`'s header for the exact mechanism and
 * file:line; this type's residual is the same one, not a new one.
 */
import { z } from 'zod';

export const ONBOARDING_MARK_TYPE_CODE = 'onboarding-mark';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

/** Cleaned the same way `clientAbout.ts`'s `AboutSource.name` is: `safeDisplayText`,
 *  whitespace collapsed, trimmed, at the façade before this is ever constructed. */
const markedByName = z.string().min(1).max(200);

/** Who marked the client, as the façade's WIRE shape carries it (`mark.markedBy`, and the list
 *  route's `markedOnboarded.markedBy`). Not the stored shape: see {@link OnboardingMarkAttributesSchema}. */
export const OnboardingMarkedBySchema = z.strictObject({
  userId: id,
  name: markedByName,
});

export type OnboardingMarkedBy = z.infer<typeof OnboardingMarkedBySchema>;

/**
 * The STORED row. Every field is a flat string (crossmarc/steward-app#1859): the type is
 * `strict` in appcore, whose compile rule has no object kind and reads `array` as `string[]`
 * (appcore `packages/core/src/db/zod-compiler.ts`), so the nested `markedBy` object #1766 first
 * tried to store was refused on every write. There is no fallback read of that old nested
 * shape, because appcore never accepted one: the refusal is what #1859 is. Consistent with that,
 * a drained scan of `appcore-dev` on 2026-09-26 (the #1859 session's, with a positive control)
 * found 0 `onboarding-mark` rows, and the coordinator reported #1766 as never tagged to stage
 * or prod at that date.
 */
export const OnboardingMarkAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this mark belongs to (that type's own appcore `instanceId`). See
   *  this file's header, "One row per client record, found by attribute". */
  clientId: id,
  markedByUserId: id,
  markedByName,
  markedAt: instant,
});

export type OnboardingMarkAttributes = z.infer<typeof OnboardingMarkAttributesSchema>;

/** The wire `markedBy` for a stored mark. */
export function onboardingMarkedBy(mark: OnboardingMarkAttributes): OnboardingMarkedBy {
  return { userId: mark.markedByUserId, name: mark.markedByName };
}

/** Reads a stored `onboarding-mark` row's attributes. `unreadable` covers both "does not parse"
 *  and "is missing", matching `readClientAbout`'s and `readAdvisorClient`'s convention. */
export function readOnboardingMark(
  value: unknown,
): { kind: 'mark'; mark: OnboardingMarkAttributes } | { kind: 'unreadable' } {
  const parsed = OnboardingMarkAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'mark', mark: parsed.data } : { kind: 'unreadable' };
}
